import { useEffect, useState } from 'react';
import { Button, Label, Select, ListGroup, Modal, TextInput } from 'flowbite-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useAddGrade, useAddSubject, useSchoolGradeSubjects, useSchoolGrades, useSubjectsList, useSearchGrades, useJoinGrade } from '../../react-query/queries';
import { getUserInfo, adminGetInstitutionFields, AdminField, adminGetFieldAcademicYears, adminCreateAcademicYear, AdminAcademicYear, adminGetAcademicYearCourses } from '../../utils/remote';
import { getPresetInstitutionGradeSubjects } from '../../utils/remote';
import { useNavigate } from 'react-router-dom';

const ClassSetup = () => {
  const { token, role } = useAuth();
  const isAllowed = !!token && (role === 'superuser' || role === 'school' || role === 'college_university');
  const [gradeId, setGradeId] = useState<number | null>(null);
  const [newGrade, setNewGrade] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
  const [gradeSearchTerm, setGradeSearchTerm] = useState('');
  const [gradeSuggestions, setGradeSuggestions] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<{ id: number; name: string } | null>(null);
  const [presetSubjects, setPresetSubjects] = useState<{ id: number; name: string; institutions?: number[] }[]>([]);
  const [selectedPresetIds, setSelectedPresetIds] = useState<number[]>([]);

  const { data: gradesData, isFetching: gradesLoading, refetch: refetchGrades } = useSchoolGrades(token);
  const { data: subsData, isFetching: subsLoading, refetch: refetchSubs } = useSchoolGradeSubjects(token, gradeId ? String(gradeId) : '0');
  const { data: allSubjectsData } = useSubjectsList(token);
  const addGrade = useAddGrade();
  const searchGrades = useSearchGrades();
  const joinGrade = useJoinGrade();
  const addSubject = useAddSubject();
  const [userInfo, setUserInfo] = useState<any>(null);
  const [fieldsData, setFieldsData] = useState<AdminField[] | null>(null);
  const [academicYears, setAcademicYears] = useState<AdminAcademicYear[] | null>(null);
  const [newAcademicYearName, setNewAcademicYearName] = useState('');
  const [expandedYearId, setExpandedYearId] = useState<number | null>(null);
  const [coursesByYear, setCoursesByYear] = useState<Record<number, { id: number; name: string }[]>>({});
  const [loadingYearCourses, setLoadingYearCourses] = useState<number | null>(null);

  const isUniversity = (() => {
    const instType = userInfo?.institution_type ?? (role === 'college_university' ? 'University/College' : '');
    const t = String(instType || '').toLowerCase();
    return t.includes('university') || t.includes('college') || role === 'college_university';
  })();

  useEffect(() => {
    if (gradesData?.grades?.length && gradeId == null) {
      setGradeId(gradesData.grades[0].id);
    }
  }, [gradesData, gradeId]);

  useEffect(() => {
    if (searchTerm && allSubjectsData?.subjects) {
      const filtered = allSubjectsData.subjects.filter((s: any) =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  }, [searchTerm, allSubjectsData]);

  // Debounced grade search
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!isGradeModalOpen) return;
      try {
        const q = gradeSearchTerm.trim();
        const res = await searchGrades.mutateAsync({ toBeValidated: token, q });
        setGradeSuggestions(Array.isArray(res) ? res : []);
      } catch (e) {
        setGradeSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [gradeSearchTerm, isGradeModalOpen]);

  useEffect(() => {
    // fetch basic user info including institution type/name stored by backend
    const fetch = async () => {
      try {
        const data = await getUserInfo();
        setUserInfo(data);
      } catch (e) {
        // ignore
      }
    };
    fetch();
  }, []);

  // When userInfo is loaded, if institution is university/college load departments (fields)
  useEffect(() => {
    let mounted = true;
    const loadFields = async () => {
      try {
        const instType = (userInfo?.institution_type || (role === 'school' ? 'School' : (role === 'college_university' ? 'University/College' : null)));
        const instId = userInfo?.institution_id ?? null;
        if (instId && instType && (String(instType).toLowerCase().includes('university') || String(instType).toLowerCase().includes('college'))) {
          const fields = await adminGetInstitutionFields(instId);
          if (!mounted) return;
          setFieldsData(fields || []);
          // if no gradeId selected, set first field as selected for quick UX
          if ((fields || []).length && gradeId == null) setGradeId((fields[0] && fields[0].id) || null);
        } else {
          if (mounted) setFieldsData(null);
        }
      } catch (e) {
        console.error('Failed to load institution fields', e);
        if (mounted) setFieldsData([]);
      }
    };
    loadFields();
    return () => { mounted = false; }
  }, [userInfo, role]);

  // For university flow, load academic years for selected department (gradeId holds department id)
  useEffect(() => {
    let mounted = true;
    const instId = userInfo?.institution_id ?? null;
    if (isUniversity && instId && gradeId) {
      (async () => {
        try {
          const years = await adminGetFieldAcademicYears(instId, gradeId);
          if (!mounted) return;
          setAcademicYears(years || []);
        } catch (e) {
          console.error('Failed to load academic years', e);
          if (mounted) setAcademicYears([]);
        }
      })();
    } else {
      if (mounted) setAcademicYears(null);
    }
    return () => { mounted = false; }
  }, [isUniversity, userInfo, gradeId]);

  

  if (!isAllowed) return <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">403 - Not authorized</div>;

  const submitGrade = async () => {
    if (!newGrade.trim()) return;
    // If a grade suggestion was selected (we'll detect by matching name), prefer join
    const matched = gradeSuggestions.find((g) => (g.name || g.grade_name || '').toLowerCase() === newGrade.trim().toLowerCase());
    if (matched) {
      try {
        await joinGrade.mutateAsync({ toBeValidated: token, gradeId: matched.id });
      } catch (e) {
        // fall back to create
        await addGrade.mutateAsync({ toBeValidated: token, name: newGrade.trim() });
      }
    } else {
      await addGrade.mutateAsync({ toBeValidated: token, name: newGrade.trim() });
    }
    setNewGrade('');
    refetchGrades();
    setIsGradeModalOpen(false);
  };

  const submitSubject = async () => {
    if (!gradeId) return;

    if (selectedSubject) {
      // Associate existing subject
      await addSubject.mutateAsync({ toBeValidated: token, gradeId: gradeId, subjectId: selectedSubject.id });
    } else if (newSubject.trim()) {
      // Create new subject
      await addSubject.mutateAsync({ toBeValidated: token, gradeId: gradeId, name: newSubject.trim() });
    } else {
      return; // No subject selected or typed
    }

    setNewSubject('');
    setSearchTerm('');
    setSelectedSubject(null);
    refetchSubs();
    setIsSubjectModalOpen(false);
  };

  const handleSubjectSelect = (subject: { id: number; name: string }) => {
    setSelectedSubject(subject);
    setSearchTerm(subject.name);
    setNewSubject(subject.name); // Also set newSubject for display in TextInput
    setFilteredSuggestions([]);
  };

  const navigate = useNavigate();

  const goToChapters = (subjectId: number) => {
    const params = new URLSearchParams();
    params.set('subjectId', String(subjectId));
    if (gradeId) params.set('gradeId', String(gradeId));
    navigate(`/school-setup/chapters?${params.toString()}`);
  };

  return (
    <div className="grid grid-cols-12 gap-5">
      {/* Grades / Departments Card */}
      <div className="col-span-12 lg:col-span-4">
        <div className="w-full bg-white px-5 pt-6 pb-4 shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">{isUniversity ? 'Departments' : 'Grades'}</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{isUniversity ? 'Create and select a department to manage its subjects.' : 'Create and select a grade to manage its subjects.'}</p>
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">{isUniversity ? (fieldsData?.length ?? 0) : (gradesData?.grades?.length ?? 0)} total</span>
          </div>
          <div className="mt-4 flex justify-end">
            <Button size="sm" onClick={() => setIsGradeModalOpen(true)}>{isUniversity ? 'Add Department' : 'Add Grade'}</Button>
          </div>
          <div className="mt-4">
            {isUniversity ? (
              fieldsData === null ? (
                <p className="text-sm text-gray-500">Loading departments…</p>
              ) : fieldsData.length ? (
                <ListGroup className="justify-center border-gray border-b-gray">
                  {fieldsData.map((f: AdminField) => (
                    <ListGroup.Item
                      key={f.id}
                      className={`border-b-gray pt-1 pb-1 grade-choice`}
                      onClick={() => setGradeId(f.id)}
                      active={gradeId === f.id ? true : false}
                    >
                      <span className="text">{f.name}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">No departments yet. Add your first department above.</div>
              )
            ) : (
              gradesLoading ? (
                <p className="text-sm text-gray-500">Loading grades…</p>
              ) : gradesData?.grades?.length ? (
                <ListGroup className="justify-center border-gray border-b-gray">
                  {gradesData.grades.map((g: any) => (
                    <ListGroup.Item
                      key={g.id}
                      className={`border-b-gray pt-1 pb-1 grade-choice`}
                      onClick={() => setGradeId(g.id)}
                      active={gradeId === g.id ? true : false}
                    >
                      <span className="text">Grade {g.grade_name || g.name}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">No grades yet. Add your first grade above.</div>
              )
            )}
          </div>
        </div>
      </div>
      {/* Subjects Card */}
      <div className="col-span-12 lg:col-span-8">
        <div className="w-full bg-white px-5 pt-6 pb-4 shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">Subjects</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Subjects under the selected grade.</p>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="gradeSel" value={isUniversity ? 'Department' : 'Grade'} className="mr-1" />
              <Select id="gradeSel" value={gradeId ?? ''} onChange={(e) => setGradeId(Number(e.target.value))}>
                {isUniversity ? (
                  fieldsData?.map((f: AdminField) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))
                ) : (
                  gradesData?.grades?.map((g: any) => (
                    <option key={g.id} value={g.id}>{g.grade_name || g.name}</option>
                  ))
                )}
              </Select>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={async () => {
                // Open preset modal (load presets first)
                if (!gradeId) return;
                const instType = (userInfo?.institution_type || (role === 'school' ? 'School' : (role === 'college_university' ? 'University/College' : null)));
                const presetName = instType && String(instType).toLowerCase().includes('school') ? 'eltest-school' : 'eltest-uni';
                try {
                  const items = await getPresetInstitutionGradeSubjects(presetName, gradeId);
                  setPresetSubjects(items || []);
                  setSelectedPresetIds([]);
                  setIsPresetModalOpen(true);
                } catch (e) {
                  setPresetSubjects([]);
                  setSelectedPresetIds([]);
                  setIsPresetModalOpen(true);
                }
              }} disabled={!gradeId}>Load preset</Button>
              <Button size="sm" onClick={() => setIsSubjectModalOpen(true)} disabled={!gradeId}>{isUniversity ? 'Add Academic Year' : 'Add Subject'}</Button>
            </div>
          </div>
          <div className="mt-4">
            {isUniversity ? (
              academicYears === null ? (
                <p className="text-sm text-gray-500">Loading academic years…</p>
              ) : academicYears.length ? (
                <ul className="space-y-2">
                  {academicYears.map((y: AdminAcademicYear) => (
                    <li key={y.id} className="border border-gray-100 rounded">
                      <button
                        type="button"
                        onClick={async () => {
                          // toggle expand/collapse
                          if (expandedYearId === y.id) {
                            setExpandedYearId(null);
                            return;
                          }
                          setExpandedYearId(y.id);
                          // if we don't have courses for this year yet, fetch them
                          if (!coursesByYear[y.id]) {
                            const instId = userInfo?.institution_id;
                            if (!instId) return;
                            try {
                              setLoadingYearCourses(y.id);
                              const courses = await adminGetAcademicYearCourses(instId, gradeId as number, y.id);
                              setCoursesByYear(prev => ({ ...prev, [y.id]: courses || [] }));
                            } catch (e) {
                              console.error('Failed to load courses for academic year', e);
                              setCoursesByYear(prev => ({ ...prev, [y.id]: [] }));
                            } finally {
                              setLoadingYearCourses(null);
                            }
                          }
                        }}
                        className="w-full text-left px-3 py-2 flex items-center justify-between gap-3"
                      >
                        <span className="font-medium">{y.name}</span>
                        <div className="flex items-center gap-2">
                          {loadingYearCourses === y.id ? <span className="text-sm text-gray-500">Loading...</span> : null}
                          <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200">Academic Year</span>
                        </div>
                      </button>
                      {/* Expanded course list */}
                      {expandedYearId === y.id && (
                        <div className="pl-4 pb-2 pt-2">
                          {coursesByYear[y.id] == null ? (
                            <p className="text-sm text-gray-500">Loading courses…</p>
                          ) : coursesByYear[y.id].length ? (
                            <ul className="space-y-1">
                              {coursesByYear[y.id].map((c) => (
                                <li key={c.id} className="flex items-center justify-between rounded px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                                  <span>{c.name}</span>
                                  <button className="text-xs text-blue-600 hover:underline" onClick={() => goToChapters(c.id)}>Manage</button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="text-sm text-gray-500">No courses for this academic year.</div>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">No academic years for this department yet. Add one above.</div>
              )
            ) : (
              subsLoading ? (
                <p className="text-sm text-gray-500">Loading subjects…</p>
              ) : (subsData?.subjects?.length ? (
                <ul className="space-y-2">
                  {subsData.subjects.map((s: any) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between rounded border border-gray-100 px-3 py-2 text-sm dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => goToChapters(s.id)}
                      title="Manage chapters"
                    >
                      <span className="font-medium">{s.subject_name || s.name}</span>
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200">Subject</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">No subjects for this grade yet. Add one above.</div>
              ))
            )}
          </div>
        </div>
      </div>

  <Modal show={isGradeModalOpen} onClose={() => setIsGradeModalOpen(false)} className="z-[9999]" style={{ zIndex: 9999 }}>
        <Modal.Header>Add Grade</Modal.Header>
        <Modal.Body>
            <div className="space-y-4 relative min-h-[320px]">
            <Label htmlFor="newGrade" value="Grade name" />
            <TextInput id="newGrade" value={newGrade} onChange={(e) => { setNewGrade(e.target.value); setGradeSearchTerm(e.target.value); }} placeholder="e.g., 1, 2, 3 or Grade 1" />
              {isGradeModalOpen && gradeSuggestions.length > 0 && (
                <ListGroup className="absolute z-50 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-700 shadow-lg rounded-md mt-1">
                {gradeSuggestions.map((g: any) => (
                  <ListGroup.Item key={g.id} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={async () => {
                    // Join this grade immediately
                    try {
                      await joinGrade.mutateAsync({ toBeValidated: token, gradeId: g.id });
                      setIsGradeModalOpen(false);
                      refetchGrades();
                      setNewGrade('');
                    } catch (e) {
                      // ignore errors
                    }
                  }}>
                    {g.grade_name || g.name}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={submitGrade} isProcessing={addGrade.isPending} disabled={!newGrade.trim()}>Save</Button>
          <Button color="gray" onClick={() => setIsGradeModalOpen(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

  <Modal show={isSubjectModalOpen} onClose={() => setIsSubjectModalOpen(false)} className="z-[9999]" style={{ zIndex: 9999 }}>
        <Modal.Header>{isUniversity ? 'Add Academic Year' : 'Add Subject'}</Modal.Header>
        <Modal.Body>
          <div className="space-y-2">
            <p className="text-sm text-gray-500">For {isUniversity ? 'department' : 'grade'}: {isUniversity ? fieldsData?.find((f) => f.id === gradeId)?.name || '-' : gradesData?.grades?.find((g: any) => g.id === gradeId)?.grade_name || gradesData?.grades?.find((g: any) => g.id === gradeId)?.name || '-'}</p>
            {/* presets moved to separate modal */}
            <Label htmlFor="subjectSearch" value={isUniversity ? 'Academic year name' : 'Subject name'} />
            <TextInput
              id="subjectSearch"
              value={isUniversity ? newAcademicYearName : searchTerm}
              onChange={(e) => {
                if (isUniversity) {
                  setNewAcademicYearName(e.target.value);
                } else {
                  setSearchTerm(e.target.value);
                  setSelectedSubject(null); // Clear selected subject on input change
                  setNewSubject(e.target.value); // Keep newSubject updated for new subject creation
                }
              }}
              placeholder={isUniversity ? "e.g., 2024/2025" : "Type to search or add new subject..."}
            />
            {!isUniversity && searchTerm && filteredSuggestions.length > 0 && (
              <ListGroup className="absolute z-10 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-700 shadow-lg rounded-md mt-1">
                {filteredSuggestions.map((s: any) => (
                  <ListGroup.Item key={s.id} onClick={() => handleSubjectSelect(s)} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                    {s.name}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={async () => {
            if (!gradeId) return;
            if (isUniversity) {
              // Create academic year for the selected department
              if (!newAcademicYearName.trim()) return;
              try {
                const instId = userInfo?.institution_id;
                if (!instId) return;
                await adminCreateAcademicYear(instId, gradeId, newAcademicYearName.trim());
                // refresh academic years
                const years = await adminGetFieldAcademicYears(instId, gradeId);
                setAcademicYears(years || []);
                setNewAcademicYearName('');
                setIsSubjectModalOpen(false);
              } catch (e) {
                console.error('Failed to create academic year', e);
              }
            } else {
              // If there are selected presets, add them first
              if (selectedPresetIds.length > 0 && gradeId) {
                for (const sid of selectedPresetIds) {
                  try {
                    await addSubject.mutateAsync({ toBeValidated: token, gradeId: gradeId, subjectId: sid });
                  } catch (e) {
                    // continue
                  }
                }
              }
              // Then handle single selection/new subject as before
              await submitSubject();
            }
          }} isProcessing={addSubject.isPending} disabled={isUniversity ? !newAcademicYearName.trim() || !gradeId : ((!selectedSubject && !newSubject.trim() && selectedPresetIds.length === 0) || !gradeId)}>Save</Button>
          <Button color="gray" onClick={() => setIsSubjectModalOpen(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Preset modal */}
  <Modal show={isPresetModalOpen} onClose={() => setIsPresetModalOpen(false)} className="z-[9999]" style={{ zIndex: 9999 }}>
        <Modal.Header>Load Preset Subjects</Modal.Header>
        <Modal.Body>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Choose preset subjects to add for grade: {gradesData?.grades?.find((g: any) => g.id === gradeId)?.grade_name || '-'}</p>
              <div>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:underline"
                  onClick={() => {
                    // compute selectable ids (not already added)
                    const selectable = presetSubjects.filter((p) => !(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id))).map((p) => p.id);
                    const allSelected = selectable.every((id) => selectedPresetIds.includes(id)) && selectable.length > 0;
                    if (allSelected) {
                      // unselect all
                      setSelectedPresetIds((s) => s.filter((id) => !selectable.includes(id)));
                    } else {
                      // select all (merge)
                      setSelectedPresetIds((s) => Array.from(new Set([...s, ...selectable])));
                    }
                  }}
                >
                  {(() => {
                    const selectable = presetSubjects.filter((p) => !(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id))).map((p) => p.id);
                    const allSelected = selectable.every((id) => selectedPresetIds.includes(id)) && selectable.length > 0;
                    return allSelected ? 'Unselect all' : 'Select all';
                  })()}
                </button>
              </div>
            </div>
            {presetSubjects.length > 0 ? (
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {presetSubjects.map((p) => {
                  const already = !!(userInfo?.institution_id && Array.isArray(p.institutions) && p.institutions.includes(userInfo.institution_id));
                  return (
                    <label key={p.id} className={`flex items-center gap-2 p-2 border rounded ${already ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={selectedPresetIds.includes(p.id)}
                        disabled={already}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedPresetIds((s) => [...s, p.id]);
                          else setSelectedPresetIds((s) => s.filter((id) => id !== p.id));
                        }}
                      />
                      <span>{p.name}{already ? ' (already added)' : ''}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No preset subjects available for this grade.</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={async () => {
            if (!gradeId) return;
            if (selectedPresetIds.length > 0) {
              for (const sid of selectedPresetIds) {
                try {
                  await addSubject.mutateAsync({ toBeValidated: token, gradeId: gradeId, subjectId: sid });
                } catch (e) {
                  // ignore
                }
              }
            }
            refetchSubs();
            setSelectedPresetIds([]);
            setIsPresetModalOpen(false);
          }} isProcessing={addSubject.isPending} disabled={!gradeId}>Save</Button>
          <Button color="gray" onClick={() => setIsPresetModalOpen(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default ClassSetup;
